/**
 * The preconditions both modes share, ported from `feat/alerts-list-querying`.
 *
 * Every alert request is tenant- and location-scoped, so there are three ways to
 * have nothing to ask for — no organization, no loadable locations, or an
 * organization with no location scope at all. Each is a different problem with a
 * different fix, and collapsing them into one spinner leaves an operator staring
 * at a blank page with no idea which one they are in.
 */
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import { Skeleton } from "@/ui/components/ui/skeleton";
import type { TLocation } from "@/locations/types/location.ts";

const Notice = ({
  tone,
  title,
  hint,
}: {
  tone: "warning" | "error";
  title: string;
  hint: string;
}) => (
  <div className="flex h-full min-h-64 flex-col items-center justify-center gap-2 p-6 text-center">
    <AlertTriangle
      className={tone === "error" ? "h-6 w-6 text-destructive" : "h-6 w-6 text-amber-500"}
    />
    <p className="text-sm font-medium">{title}</p>
    <p className="max-w-sm text-xs text-muted-foreground">{hint}</p>
  </div>
);

interface AlertsScopeGateProps {
  organizationId: number | null;
  locations: TLocation[];
  isLoading: boolean;
  isError: boolean;
  children: ReactNode;
}

export const AlertsScopeGate = ({
  organizationId,
  locations,
  isLoading,
  isError,
  children,
}: AlertsScopeGateProps) => {
  const { t } = useTranslation("alerts");

  if (organizationId == null) {
    return (
      <Notice
        tone="warning"
        title={t("scopeGate.noOrganization.title")}
        hint={t("scopeGate.noOrganization.hint")}
      />
    );
  }
  if (isError) {
    return (
      <Notice
        tone="error"
        title={t("scopeGate.locationsError.title")}
        hint={t("scopeGate.locationsError.hint")}
      />
    );
  }
  if (isLoading) {
    return (
      <div className="p-4">
        <Skeleton className="h-full min-h-64 w-full rounded-lg" />
      </div>
    );
  }
  if (locations.length === 0) {
    return (
      <Notice
        tone="warning"
        title={t("scopeGate.noLocations.title")}
        hint={t("scopeGate.noLocations.hint")}
      />
    );
  }
  return <>{children}</>;
};
